import DocumentViewerModal from "components/DocumentViewer";
import FileRow from "components/file/file-row";
import { useState } from "react";
import { LegalDrafting } from "types";
import { formatDay } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface SingleLegalDraftingPaperDetailPops {
  personImg: any;
  research: LegalDrafting;
  fileRowProps?: any;
}

export default function SingleLegalDraftingPapersDetail({
  personImg,
  research,
  fileRowProps,
}: SingleLegalDraftingPaperDetailPops) {
  const { t } = useTranslation();

  // Function to get the file to display based on priority rules
  const getFileToDisplay = () => {
    const allFiles = research?.uploaded_files || [];

    // Find and sort amended law files (priority 1)
    const amendedFiles = allFiles
      .filter((doc: any) => doc.document_type === "amended_law" || doc.document_type === "amended_law_file")
      .sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    if (amendedFiles.length > 0) {
      return amendedFiles; // Return all amended files, latest first
    }

    // Find and sort draft files (priority 2)
    const draftFiles = allFiles
      .filter((doc: any) => doc.document_type === "law_draft")
      .sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    if (draftFiles.length > 0) {
      return draftFiles; // Return all draft files, latest first
    }

    return [];
  };

  // Get the files to display
  const filesToDisplay = getFileToDisplay();

  return (
    <>
      <div className="flex flex-col bg-white rounded-lg shadow-md px-8 p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-semibold text-3xl text-[#212121] mb-1">
              {t("PublishDraftLegislationForPublic")}
            </h2>
          </div>
        </div>

        <div className="flex gap-1 text-[#9D0303] font-medium text-sm mb-1">
          <p>{formatDay(research.created_at).weekday},</p>
          <p>{formatDay(research.created_at).rest}</p>
        </div>

        {research.request_last_updated_date && (
          <div className="text-xs text-gray-500 mb-2">
            {t("LastUpdated")}: {formatDay(research.request_last_updated_date).weekday}, {formatDay(research.request_last_updated_date).rest}
          </div>
        )}

        {/* Draft Summary with proper paragraph formatting */}
        <div className="mt-4 mb-6">
          {research.draft_summary ? (
            <div className="space-y-4">
              {research.draft_summary.split("\n").map(
                (paragraph: string, index: number) =>
                  paragraph.trim() !== "" && (
                    <p
                      key={index}
                      className="font-light text-base text-[#212121] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  )
              )}
            </div>
          ) : (
            <p className="font-light text-base text-gray-500 italic">
              {t("NoDraftSummaryAvailable")}
            </p>
          )}
        </div>

        {/* Document Viewer Section */}
        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg gap-2 flex-col mb-2">
          {filesToDisplay.length > 0 && (
            <>
              <DocumentViewerModal
                documents={[filesToDisplay[0]]}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legislative_drafting/drafting-files/document/`}
              />
              {filesToDisplay.length > 1 && (
                <details className="my-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    {t("PreviousVersions", { count: filesToDisplay.length - 1 })}
                  </summary>
                  <div className="mt-2">
                    <DocumentViewerModal
                      documents={filesToDisplay.slice(1)}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}legislative_drafting/drafting-files/document/`}
                    />
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}